import { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";

import {
  OverviewAvgActiveChart,
  OverviewUniqueTvChart,
  OverviewSwitchInChart
} from "../../components/charts3";

import "./ChannelOverview.css";

import { useImpressionsData } from "../../hooks/useImpressionsData";
import { useLiveData } from "../../hooks/useLiveData";
import { useDaysSorted } from "../../hooks/useDaysSorted";
import { useYesterdayMetrics } from "../../hooks/useYesterdayMetrics";
import { useUniqueTvData } from "../../hooks/useUniqueTvData";
import { useOverviewKpis } from "../../hooks/useOverviewKpis";
import { formatNumber } from "../../utils/formatNumber";

import { useUserJourneyLogger } from "../../hooks/useUserJourneyLogger";
import { useRatingEstimation } from "../../hooks/useRatingEstimation";
import OverviewRankingCard from "../../components/Channel/OverviewRankingCard";
import OverviewTopProgramsCard from "../../components/Channel/OverviewTopProgramsCard";
import { TTV_CHANNEL_ID } from "../../utils/ttvChannel";

export default function ChannelOverview({ channelId, refreshKey, channelInfo, rawVideo }) {

  const storedUser = JSON.parse(
    localStorage.getItem("adm_panel_user") || "{}"
  );

  const isDark = !!storedUser.darkmode;
  const TTVOverview = !!storedUser.ttvoverview;

  // Same rule the sidebar applies: the feature has to be granted to both the
  // user and the channel. Without this the card would appear on every Overview,
  // including channels whose menu has no ranking page to open.
  const showRanking = useMemo(() => {
    const lower = (arr) => (arr || []).map((s) => String(s).toLowerCase());
    const userItems = lower(storedUser.sidebaritems);
    const channelItems = lower(channelInfo?.sidebarItems || channelInfo?.sidebaritems);
    return userItems.includes("liveranking") && channelItems.includes("liveranking");
  }, [storedUser.sidebaritems, channelInfo]);

  const { logPageChange } = useUserJourneyLogger({
    channelId,
    channelName: channelInfo?.name || channelInfo?.n || "unknown",
  });

  useEffect(() => {
    logPageChange({
      chartType: "overview",
      mode: "default",
    });
  }, [logPageChange]);

  const [compareWithPreviousWeek, setCompareWithPreviousWeek] = useState(false);

  const estimatedRating = useRatingEstimation(channelId);

  const { liveValue, serverMinuteIndex } = useLiveData(channelId);
  const { liveValue: ttvLiveValue } = useLiveData(TTVOverview ? TTV_CHANNEL_ID : null);
  const { rawImpressions } = useImpressionsData(channelId, refreshKey);

  const uniqueTvStartDate = compareWithPreviousWeek
    ? dayjs().subtract(14, "day").format("YYYY-MM-DD")
    : dayjs().subtract(7, "day").format("YYYY-MM-DD");

  const { data: uniqueTvData } = useUniqueTvData(channelId, uniqueTvStartDate);

  const videoKeys = useDaysSorted(rawVideo);
  const impKeys = useDaysSorted(rawImpressions);

  const yesterdayKey = videoKeys?.at(-2) || null;
  const yesterdayImpKey = impKeys?.at(-2) || null;

  const weekBeforeKey = videoKeys?.at(-9) || null;
  const weekBeforeImpKey = impKeys?.at(-9) || null;

  const { avgActiveYesterday, switchInYesterday } =
    useYesterdayMetrics(rawVideo, rawImpressions, yesterdayKey, yesterdayImpKey);

  const { avgActiveYesterday: avgActiveWeekBefore, switchInYesterday: switchInWeekBefore } =
    useYesterdayMetrics(rawVideo, rawImpressions, weekBeforeKey, weekBeforeImpKey);

  /* -----------------------------
     LIVE vs LAST WEEK SAME TIME
     Logic mirrors the old panel:
       - date  : exactly 7 days ago
       - index : x-server-minute-index header from /live response
       - divisor: 2  (matches old panel's Math.round(value / 2))
  ----------------------------- */
  const lastWeekLiveValue = useMemo(() => {
    if (!rawVideo) return null;

    const sevenDaysAgo = dayjs().subtract(7, "day");
    const keyDD   = sevenDaysAgo.format("DD-MM-YYYY");
    const keyYYYY = sevenDaysAgo.format("YYYY-MM-DD");

    const data = rawVideo[keyDD] ?? rawVideo[keyYYYY];
    if (!Array.isArray(data) || data.length !== 1440) return null;

    // prefer server-side minute index from header, fall back to client clock
    const minuteIdx = serverMinuteIndex ?? (new Date().getHours() * 60 + new Date().getMinutes());

    return Math.round((data[minuteIdx] || 0) / 2);
  }, [rawVideo, serverMinuteIndex]);

  const liveVsLastWeek = useMemo(() => {
    if (lastWeekLiveValue === null || lastWeekLiveValue === 0) return null;
    const pct = (((liveValue - lastWeekLiveValue) / lastWeekLiveValue) * 100).toFixed(1);
    return { lastWeek: lastWeekLiveValue, pct: Number(pct) };
  }, [liveValue, lastWeekLiveValue]);

  const {
    videoConsumedYesterday,
    switchInYesterday: switchInYesterdayKpi,
    uniqueTvYesterday,
    loading: kpiLoading,
  } = useOverviewKpis(channelId);

  /* -----------------------------
     UNIQUE TV SERIES
  ----------------------------- */

  const uniqueLast7Series = (() => {
    if (!uniqueTvData || !videoKeys?.length) return [];

    const u = uniqueTvData?.data ?? uniqueTvData;
    const base = dayjs(yesterdayKey, ["DD-MM-YYYY", "YYYY-MM-DD"], true);
    if (!base.isValid()) return [];

    return Array.from({ length: 7 }, (_, i) => {
      const d = base.subtract(6 - i, "day");
      const keyDD = d.format("DD-MM-YYYY");
      const keyYYYY = d.format("YYYY-MM-DD");

      const weekBeforeDate = d.subtract(7, "day");
      const weekBeforeDisplay = weekBeforeDate.format("DD-MM");
      const currentDisplay = d.format("DD-MM");

      const label = compareWithPreviousWeek
        ? `${currentDisplay}/${weekBeforeDisplay}`
        : keyDD;

      return {
        label,
        value: Number(u?.[keyDD] ?? u?.[keyYYYY] ?? 0),
      };
    });
  })();

  const uniqueWeekBeforeSeries = (() => {
    if (!compareWithPreviousWeek || !uniqueTvData || !videoKeys?.length) return [];

    const u = uniqueTvData?.data ?? uniqueTvData;
    const baseYesterday = dayjs(yesterdayKey, ["DD-MM-YYYY", "YYYY-MM-DD"], true);
    if (!baseYesterday.isValid()) return [];

    return Array.from({ length: 7 }, (_, i) => {
      const currentDate = baseYesterday.subtract(6 - i, "day");
      const weekBeforeDate = currentDate.subtract(7, "day");

      const weekBeforeDD = weekBeforeDate.format("DD-MM-YYYY");
      const weekBeforeYYYY = weekBeforeDate.format("YYYY-MM-DD");

      const currentDisplay = currentDate.format("DD-MM");
      const weekBeforeDisplay = weekBeforeDate.format("DD-MM");

      return {
        label: `${currentDisplay}/${weekBeforeDisplay}`,
        value: Number(u?.[weekBeforeDD] ?? u?.[weekBeforeYYYY] ?? 0),
      };
    });
  })();

  /* -----------------------------
     RENDER
  ----------------------------- */

  return (
    <>
      {channelId === "AwG6zFgabB" && (
        <div className="overview-info-bars">
          <div className="overview-info-bar">
            <span className="info-bar-icon">📊</span>
            <span className="info-bar-label">Rating estimation:</span>
            <span className="info-bar-value">
              {estimatedRating !== null ? estimatedRating : "…"}
            </span>
          </div>
          <div className="overview-info-bar">
            <span className="info-bar-icon">🥧</span>
            <span className="info-bar-label">Pool share:</span>
            <span className="info-bar-value">Not active</span>
          </div>
        </div>
      )}

      <div className={`overview-cards ${showRanking ? "has-ranking" : ""}`}>

        {/* ACTIVE TV */}
        <div className="overview-card card-red">
          {/* Font Awesome throughout rather than emoji: emoji are drawn by the
              operating system in their own colours and weights, so the six
              badges never matched each other. A glyph inherits the card's own
              white and one stroke weight. */}
          <div className="card-icon"><i className="fa-solid fa-clock" /></div>

          <div className="card-number">
            {formatNumber(liveValue)}
            {TTVOverview && (
              <>
                <span className="ttv-sep">/</span>
                <span className="ttv-value">{formatNumber(ttvLiveValue)}</span>
              </>
            )}
          </div>

          <div className="card-title">
            {TTVOverview ? "active TV / TMTV" : "active TV"}
          </div>
          {liveVsLastWeek && (
            <div className="card-subtitle">
              Last week: {formatNumber(liveVsLastWeek.lastWeek)}{" "}
              <span style={{ fontWeight: 600 }}>
                ({liveVsLastWeek.pct >= 0 ? "+" : ""}{liveVsLastWeek.pct}%)
              </span>
            </div>
          )}
        </div>

        {/* VIDEO CONSUMED */}
        <div className="overview-card card-green">
          <div className="card-icon"><i className="fa-solid fa-play" /></div>
          <div className="card-number">
            {kpiLoading ? "…" : formatNumber(videoConsumedYesterday)} hrs
          </div>
          <div className="card-title">video consumed yesterday</div>
        </div>

        {/* SWITCH IN */}
        <div className="overview-card card-orange">
          <div className="card-icon"><i className="fa-solid fa-eye" /></div>
          <div className="card-number">
            {kpiLoading ? "…" : formatNumber(switchInYesterdayKpi)}
          </div>
          <div className="card-title">switch-in yesterday</div>
        </div>

        {/* UNIQUE TV */}
        <div className="overview-card card-blue">
          <div className="card-icon"><i className="fa-solid fa-desktop" /></div>
          <div className="card-number">
            {kpiLoading ? "…" : formatNumber(uniqueTvYesterday)}
          </div>
          <div className="card-title">unique TV yesterday</div>
        </div>

   
        {showRanking && (
          <>
            <OverviewRankingCard channelId={channelId} refreshKey={refreshKey} />
            <OverviewTopProgramsCard channelId={channelId} rawVideo={rawVideo} />
          </>
        )}
      </div>

      <div className={`comparison-toggle-container ${isDark ? "dark" : ""}`}>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={compareWithPreviousWeek}
            onChange={(e) => setCompareWithPreviousWeek(e.target.checked)}
          />
          <span className="toggle-slider"></span>
        </label>
        <span
          className="toggle-label"
          onClick={() => setCompareWithPreviousWeek(!compareWithPreviousWeek)}
        >
          Compare Data With Previous Week
        </span>
      </div>

      <div className="overview-charts">
        <div className="chart-box">
          <OverviewAvgActiveChart
            series={avgActiveYesterday}
            seriesWeekBefore={avgActiveWeekBefore}
            showComparison={compareWithPreviousWeek}
            mode="hour"
            chartType="bar"
          />
        </div>

        <div className="chart-box">
          {switchInYesterday?.length > 0 && (
            <OverviewSwitchInChart
              series={switchInYesterday}
              seriesWeekBefore={switchInWeekBefore}
              showComparison={compareWithPreviousWeek}
              mode="hour"
              chartType="bar"
            />
          )}
        </div>

        <div className="chart-box">
          {uniqueLast7Series.length ? (
            <OverviewUniqueTvChart
              series={uniqueLast7Series}
              seriesWeekBefore={uniqueWeekBeforeSeries}
              showComparison={compareWithPreviousWeek}
              mode="day"
              chartType="bar"
            />
          ) : (
            <div style={{ padding: 40, opacity: 0.4 }}>(no data)</div>
          )}
        </div>
      </div>
    </>
  );
}